import React from 'react'
import { Radio } from 'antd'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { ColorSwatchField, StyleTileGroup, StyleTileOption } from '@pushly/aqe/lib/components'
import { AppMessageTemplateStyle } from '@pushly/models/lib/enums/app-messages/app-message-template-style'
import { AppMessageActionType } from '@pushly/models/lib/enums/app-messages/app-message-action-type'
import { AppMessageActionStyle } from '@pushly/models/lib/enums/app-messages/app-message-action-style'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import { MutableAppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import { MutableAppMessageTemplateBannerPosition } from '@pushly/models/lib/structs'
import { Position } from '@pushly/models/lib/enums/position'
import { getClassNames } from '../../../_utils/classnames'
import { AppMessageStyleDiagram } from './app-message-style-diagram'

type AppMessageStyleSelectorProps = {
    value: MutableAppMessageUiTemplate
    onChange: (change: Partial<AppMessageUiTemplate>) => void
    /**
     * The per-field enabled flags as the user last set them, so switching away
     * from a style that forces fields off and back again restores their choice.
     */
    dirtyFieldsEnabled: { [key: string]: boolean }
    disabled: boolean
}

const STYLE_OPTIONS: StyleTileOption<AppMessageTemplateStyle>[] = [
    {
        value: AppMessageTemplateStyle.MODAL,
        label: 'Modal',
        hint: 'Centred card',
        tooltip:
            'A card centred over the app with the background dimmed. Best for offers and announcements you want acknowledged.',
        visual: <AppMessageStyleDiagram style={AppMessageTemplateStyle.MODAL} />,
    },
    {
        value: AppMessageTemplateStyle.BANNER,
        label: 'Banner',
        hint: 'Top or bottom bar',
        tooltip: 'A slim bar along the top or bottom edge. Least disruptive, and best for reminders people can ignore.',
        visual: <AppMessageStyleDiagram style={AppMessageTemplateStyle.BANNER} />,
    },
    {
        value: AppMessageTemplateStyle.FULLSCREEN,
        label: 'Fullscreen',
        hint: 'Whole screen',
        tooltip: 'Covers the whole screen. Highest impact, best saved for onboarding or a major announcement.',
        visual: <AppMessageStyleDiagram style={AppMessageTemplateStyle.FULLSCREEN} />,
    },
]

/**
 * The first decision of the Message step: which shape this message takes, and
 * the two message-level appearance properties that belong with it.
 *
 * Changing style changes what the message *is* — a banner has no header or
 * title and only one button — so the switch coerces the fields it has to and
 * remembers what it overrode.
 */
export const AppMessageStyleSelector = ({
    value,
    onChange,
    dirtyFieldsEnabled,
    disabled,
}: AppMessageStyleSelectorProps) => {
    const handleStyleChange = (style: AppMessageTemplateStyle) => {
        if (disabled) {
            return
        }

        value.style = style

        // Always reset the close action so it cannot be saved and then displayed
        // on a style that does not support it.
        value.showCloseAction = false

        const image = value.image.toMutable()
        const ctaPrimary = value.ctaPrimary.toMutable()
        const ctaSecondary = value.ctaSecondary.toMutable()
        const title = value.title.toMutable()
        const header = value.header.toMutable()

        if (style !== AppMessageTemplateStyle.BANNER) {
            value.bannerPosition = null
            image.position = Position.TOP

            title.enabled = dirtyFieldsEnabled.title
            header.enabled = dirtyFieldsEnabled.header
            ctaPrimary.enabled = dirtyFieldsEnabled.ctaPrimary
            ctaSecondary.enabled = dirtyFieldsEnabled.ctaSecondary
        } else {
            value.bannerPosition = new MutableAppMessageTemplateBannerPosition()
            value.bannerPosition.mobile = Position.TOP
            value.bannerPosition.tablet = Position.TOP

            title.enabled = false
            header.enabled = false
            image.position = Position.LEFT
            ctaPrimary.enabled = dirtyFieldsEnabled.ctaPrimary
            ctaPrimary.style = AppMessageActionStyle.TAB
            ctaSecondary.enabled = false

            // Dismiss is not a valid action for BANNER, so reset to the first
            // available BANNER action option if it was previously set.
            if (ctaPrimary.action === AppMessageActionType.CLOSE) {
                ctaPrimary.action = AppMessageActionType.OPEN_URL
            }
        }

        onChange({ ...value, image, ctaPrimary, ctaSecondary, title, header })
    }

    return (
        <div className={getClassNames('app-message-style-selector')}>
            <StyleTileGroup<AppMessageTemplateStyle>
                aria-label="Message style"
                className="app-message-style-options"
                options={STYLE_OPTIONS}
                value={value.style ?? AppMessageTemplateStyle.MODAL}
                onChange={handleStyleChange}
                disabled={disabled}
            />

            <div className="app-message-appearance-row">
                <div className="app-message-bg">
                    <span className="appearance-label">Background</span>
                    <ColorSwatchField
                        aria-label="Background color"
                        disabled={disabled}
                        value={value.bgColor ?? '#FFFFFF'}
                        onChange={(color) => onChange({ bgColor: color.toUpperCase() })}
                    />
                </div>

                {value.style === AppMessageTemplateStyle.BANNER && (
                    <div className="app-message-banner-position">
                        {/* A plain label rather than a Form.Item so it matches
                            Background beside it: an AntD form label carries its
                            own type scale and column padding, which reads as a
                            different kind of label on the same row. */}
                        <span className="appearance-label">Position</span>
                        <Radio.Group
                            aria-label="Banner position"
                            disabled={disabled}
                            buttonStyle="solid"
                            className="banner-position-group"
                            onChange={(ev) => {
                                onChange({
                                    bannerPosition: value.bannerPosition?.clone({
                                        mobile: ev.target.value,
                                        tablet: ev.target.value,
                                    }),
                                })
                            }}
                            size="small"
                            value={value.bannerPosition?.mobile ?? Position.TOP}
                            options={[Position.TOP, Position.BOTTOM].map((position) => ({
                                label: titleCase(position),
                                value: position,
                            }))}
                            optionType="button"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
